import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { MapPin, Calendar, IndianRupee, BookOpen, Clock, Users, Mail, Phone, Unlock, Coins, ArrowLeft, Shield } from 'lucide-react';
import ShareButton from '../../components/ShareButton';
import apiClient from '../../services/apiClient';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = { width: '100%', height: '400px', borderRadius: '8px' };
const libraries = ['places'];

export const TuitionPostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    fetchPostDetails();
    if (token) fetchWallet();
  }, [postId, token]);

  const fetchPostDetails = async () => {
    try {
      const data = await apiClient.get(`/tuition-posts/${postId}`);
      setPost(data);
    } catch (error) {
      toast.error('Failed to load post details');
    } finally {
      setLoading(false);
    }
  };

  const fetchWallet = async () => {
    try {
      const data = await apiClient.get('/wallet/balance');
      setWallet({ balance: data.balance || 0 });
    } catch (error) {
      console.error('Failed to fetch wallet');
    }
  };

  const handleUnlock = async () => {
    if (!token) {
      toast.error('Please login to unlock contact');
      navigate('/login');
      return;
    }

    if (wallet.balance < post.unlock_price) {
      toast.error(`Insufficient balance. Need ${post.unlock_price} coins`);
      return;
    }

    setUnlocking(true);
    try {
      const data = await apiClient.post(
        `/unlock-contact?target_type=tuition_post&target_id=${postId}`
      );

      toast.success(`Contact unlocked! ${data.coins_deducted} coins deducted`);
      fetchPostDetails();
      fetchWallet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock contact');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center"><div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div><p className="mt-4 text-gray-600">Loading...</p></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center"><p className="text-xl text-gray-600">Tuition post not found</p><Button onClick={() => navigate('/tuition-posts')} className="mt-4">Browse Posts</Button></div>
      </div>
    );
  }

  const mapCenter = post.latitude && post.longitude ? { lat: post.latitude, lng: post.longitude } : { lat: 28.6139, lng: 77.2090 };
  const isOwner = user && user.id === post.student_id;
  const needsApproval = post.review_status === 'pending';
  const isApproved = post.review_status === 'approved';

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
        <Button variant="outline" onClick={() => navigate('/tuition-posts')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Posts
        </Button>

        {needsApproval && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-200">
            <Clock className="h-4 w-4" />
            <AlertDescription>This post is under review by admin. It will be visible to others once approved and pricing is set.</AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Main Details */}
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <CardTitle className="text-lg sm:text-2xl">{post.category}</CardTitle>
                      <ShareButton
                        title={post.category}
                        text={`Check out this tuition requirement: ${post.category} - ${post.subjects?.join(', ')}`}
                        variant="outline"
                        size="sm"
                      />
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Badge variant={post.status === 'active' ? 'default' : 'secondary'}>{post.status}</Badge>
                      {isApproved && <Badge variant="outline" className="bg-green-50 text-green-700">Approved</Badge>}
                      {needsApproval && <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Pending Review</Badge>}
                      {post.posted_by_admin && <Badge className="bg-gradient-to-r from-blue-500 to-[#041f96] text-white border-0"><Shield className="h-3 w-3 mr-1" />Admin</Badge>}
                    </div>
                  </div>
                  {isApproved && !isOwner && !post.contact_unlocked && post.unlock_price > 0 && (
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Unlock Price</div>
                      <div className="flex items-center text-2xl font-bold text-blue-600">
                        <Coins className="h-5 w-5 mr-1" />
                        {post.unlock_price}
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Description</h3>
                  <p className="text-gray-600 text-sm sm:text-base">{post.description}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <BookOpen className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Subjects</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {post.subjects?.map((subject, idx) => (
                          <Badge key={idx} variant="secondary">{subject}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Board</p>
                      <p className="font-medium">{post.board?.toUpperCase()}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Start Time</p>
                      <p className="font-medium">{post.start_time?.replace('_', ' ')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Availability</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {post.availability && (
                          Array.isArray(post.availability) ? (
                            post.availability.map((day, idx) => (
                              <Badge key={idx} variant="outline">{day}</Badge>
                            ))
                          ) : (
                            <Badge variant="outline">{post.availability}</Badge>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Class Mode</p>
                      <p className="font-medium">{post.class_mode?.replace('_', ' ')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <IndianRupee className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Budget</p>
                      <p className="font-medium">₹{post.budget_amount} / {post.budget_type}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center"><MapPin className="h-5 w-5 mr-2" />Location</CardTitle>
              </CardHeader>
              <CardContent>
                {!showMap ? (
                  <div className="text-center py-8">
                    <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 mb-4">Click below to view tuition location on map</p>
                    <Button
                      variant="outline"
                      onClick={() => setShowMap(true)}
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Show Tuition Location on Map
                    </Button>
                  </div>
                ) : (
                  <>
                    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                      <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={13}>
                        {post.latitude && post.longitude && <Marker position={mapCenter} />}
                      </GoogleMap>
                    </LoadScript>
                    <div className="mt-4 space-y-2">
                      <p className="text-gray-700">{post.address}</p>
                      <p className="text-gray-600">{post.city}, {post.state} {post.pincode}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card>
              <CardHeader>
                <CardTitle>Student Contact</CardTitle>
              </CardHeader>
              <CardContent>
                {isOwner ? (
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription>This is your post. Contact details are always visible to you.</AlertDescription>
                  </Alert>
                ) : post.contact_unlocked ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-green-600">
                      <Unlock className="h-5 w-5" />
                      <span className="font-semibold">Contact Unlocked</span>
                    </div>
                    {post.student_email && (
                      <div className="flex items-start space-x-2">
                        <Mail className="h-4 w-4 mt-1 text-gray-600" />
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="font-medium">{post.student_email}</p>
                        </div>
                      </div>
                    )}
                    {post.student_phone && (
                      <div className="flex items-start space-x-2">
                        <Phone className="h-4 w-4 mt-1 text-gray-600" />
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="font-medium">{post.student_phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {!isApproved ? (
                      <Alert>
                        <AlertDescription>Contact will be available once admin approves this post.</AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">Unlock student contact details to apply for this tuition.</p>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">Your Balance</span>
                            <span className="font-bold text-lg">{wallet.balance} coins</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Unlock Price</span>
                            <span className="font-bold text-lg text-blue-600">{post.unlock_price} coins</span>
                          </div>
                        </div>
                        <Button
                          onClick={handleUnlock}
                          disabled={unlocking || wallet.balance < post.unlock_price}
                          className="w-full"
                        >
                          {unlocking ? 'Unlocking...' : `Unlock Contact (${post.unlock_price} coins)`}
                        </Button>
                        {wallet.balance < post.unlock_price && (
                          <p className="text-xs text-red-600 text-center">Insufficient balance. Please add coins to your wallet.</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Posted Info */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-gray-600 space-y-2">
                  <p><span className="font-medium">Posted:</span> {new Date(post.created_at).toLocaleDateString()}</p>
                  <p><span className="font-medium">Applications:</span> {post.applications_count || 0}</p>
                  {post.student_name && <p><span className="font-medium">Posted by:</span> {post.student_name}</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};